import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../api";
import { naturalCompare } from "../lib/naturalSort";
import {
  Loader2,
  Radio,
  MapPin,
  Ambulance,
  Clock,
  LogIn,
  Power,
  User,
} from "lucide-react";

function fmtRiyadh(ms: number): string {
  const d = new Date(ms + 3 * 60 * 60 * 1000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(
    d.getUTCDate()
  )} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

const typeColor: Record<string, string> = {
  ALS: "bg-red-100 text-red-700",
  BLS: "bg-blue-100 text-blue-700",
  CC: "bg-purple-100 text-purple-700",
};

type Unit = { _id: string; hospitalName: string; member1Name: string; member1IdNo: string; member2Name: string; member2IdNo: string; member3Name?: string; member3IdNo?: string; date: string; shiftStart: string; shiftEnd: string; shiftEndAt: number; ambulanceNo: string; ambulanceType: string; unitCode: string; loggedInAt: number };

export function UnitsPage() {
  const api = useApi();
  const qc = useQueryClient();
  const unitsQ = useQuery({ queryKey: ["units-active", api.token], queryFn: () => api.get<Unit[]>("/api/v1/units/active"), enabled: Boolean(api.token), refetchInterval: 30_000 });
  const unitsRaw = unitsQ.data;
  const endShift = async ({ id }: { id: string }) => {
    await api.post<void>(`/api/v1/units/${id}/end`);
    await qc.invalidateQueries({ queryKey: ["units-active"] });
  };
  // Natural sort by unit code: A1, A2 … A10 … A100.
  const units = useMemo(
    () =>
      unitsRaw
        ? [...unitsRaw].sort((a, b) => naturalCompare(a.unitCode, b.unitCode))
        : unitsRaw,
    [unitsRaw]
  );

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
          Active Units
        </h1>
        {units && (
          <span className="text-sm text-slate-500">
            {units.length} on shift
          </span>
        )}
      </div>

      {units === undefined ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[var(--hmg-red)]" />
        </div>
      ) : units.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-600">
          No units are currently on shift.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {units.map((u) => (
            <div
              key={u._id}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col"
            >
              <div className="flex items-center justify-between gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
                <div className="inline-flex items-center gap-2 font-semibold text-slate-900">
                  <Radio className="w-4 h-4 text-[var(--hmg-red)]" />
                  {u.unitCode}
                </div>
                <span
                  className={`text-xs font-semibold px-2 py-0.5 rounded ${
                    typeColor[u.ambulanceType] ?? "bg-slate-100 text-slate-700"
                  }`}
                  title={u.ambulanceType === "CC" ? "Critical Care" : u.ambulanceType}
                >
                  {u.ambulanceType}
                </span>
              </div>

              <div className="p-4 space-y-2 text-sm flex-1">
                <div className="flex items-center gap-2 text-slate-700">
                  <Ambulance className="w-4 h-4 text-slate-400" />
                  Ambulance {u.ambulanceNo}
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <MapPin className="w-4 h-4 text-slate-400" />
                  {u.hospitalName}
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <Clock className="w-4 h-4 text-slate-400" />
                  {u.date} · {u.shiftStart}–{u.shiftEnd}
                </div>
                <div className="flex items-center gap-2 text-slate-700">
                  <LogIn className="w-4 h-4 text-slate-400" />
                  Logged in {fmtRiyadh(u.loggedInAt)}
                </div>
                <div className="pt-2 border-t border-slate-100 space-y-1">
                  <div className="flex items-center gap-2 text-slate-700">
                    <User className="w-3.5 h-3.5 text-slate-400" />
                    {u.member1Name}{" "}
                    <span className="text-slate-400">({u.member1IdNo})</span>
                  </div>
                  {(u.member2Name || u.member2IdNo) && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      {u.member2Name}{" "}
                      <span className="text-slate-400">({u.member2IdNo})</span>
                    </div>
                  )}
                  {(u.member3Name || u.member3IdNo) && (
                    <div className="flex items-center gap-2 text-slate-700">
                      <User className="w-3.5 h-3.5 text-slate-400" />
                      {u.member3Name}{" "}
                      <span className="text-slate-400">({u.member3IdNo})</span>
                    </div>
                  )}
                </div>
                <div className="text-xs text-slate-500 pt-1">
                  Ends: {fmtRiyadh(u.shiftEndAt)}
                </div>
              </div>

              <div className="px-4 py-3 border-t border-slate-100">
                <button
                  onClick={async () => {
                    if (
                      confirm(
                        `End shift for unit ${u.unitCode} now? It will no longer show as active.`
                      )
                    ) {
                      await endShift({ id: u._id });
                    }
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 hover:bg-red-50 hover:border-red-300 hover:text-red-700 text-slate-700 text-sm font-semibold py-2"
                >
                  <Power className="w-4 h-4" /> End shift
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
